import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { HeaderLinksDataProps } from '../../components/navigation/header/Header';
import { LogoProps } from '../../components/navigation/header/Logo';
import { PrimaryHeaderThemeProps } from '../../components/navigation/header/primaryHeader/PrimaryHeader';
import { CtaButton, CtaDataProps } from '../../components/navigation/header/primaryHeader/CtaButton';
import { useTheme } from '../../../theming/useTheme';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { primaryHeaderHeight } from '../../components/navigation/header/header.utils';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { VrLineHeight } from '../../../theming/verticalRhythm/verticalRhythm';
import { AMPLogo } from './AMPLogo';
import { AMPPrimaryLinks } from './AMPPrimaryLinks';
import { EditionDataProps } from '../../components/navigation/header/primaryHeader/Editions';
import { AMPEditions } from './AMPEditions';
import { AMPMenuIcon } from './AMPMenuIcon';

/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

interface AMPPrimaryHeaderDataProps {
  primaryLinks: HeaderLinksDataProps[] | null
  primaryLogos: LogoProps
  editions: EditionDataProps[] | null
  cta: CtaDataProps | null
  shouldShowClosedMenuIcon: boolean
    pagePath: string
    editionEndpoint: string
}

type AMPPrimaryHeaderProps = AMPPrimaryHeaderDataProps & PrimaryHeaderThemeProps;

const componentConfigThemeKey = 'primaryHeader';

const getPrimaryHeaderStyle = (backgroundColor:string, vrLineHeight:VrLineHeight) => StyleSheet.create({
  wrapper: {
    backgroundColor,
    position: 'relative',
    height: `${primaryHeaderHeight}px`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 100,
  },
  itemsBasicContainer: {
    display: 'grid',
    gridAutoFlow: 'column',
    height: '100%',
    width: 'fit-content',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      gridColumnGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
    },
  },
  itemsRightContainer: {
    alignItems: 'baseline',
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] / 2}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingRight: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] / 2}px`,
    },
  },
  itemsLeftContainer: {
    [MEDIA_BREAKPOINTS.X_LARGE]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE]}px`,
    },
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingLeft: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
    },
  },
  ctaWrapper: {
    height: `${primaryHeaderHeight}px`,
    display: 'flex',
    alignItems: 'center',
  },
});

export const AMPPrimaryHeaderComponent:React.FunctionComponent<AMPPrimaryHeaderProps> = props => {
  const { primaryLinks, primaryLogos, cta, surfaceStyle, backgroundColor, vrLineHeight, shouldShowClosedMenuIcon, editions, pagePath, editionEndpoint } = props;
  const style = getPrimaryHeaderStyle(backgroundColor, vrLineHeight);
  return (
    <div className={css(style.wrapper, StyleSheet.create({ surfaceStyle }).surfaceStyle)}>
      <div className={css(style.itemsBasicContainer, style.itemsLeftContainer)}>
        { shouldShowClosedMenuIcon ? <AMPMenuIcon /> : null }
        <AMPLogo {...primaryLogos} />
        { primaryLinks ? <AMPPrimaryLinks links={primaryLinks} pagePath={pagePath} /> : null}
      </div>
      <div className={css(style.itemsBasicContainer, style.itemsRightContainer)}>
        { cta ? (
          <div className={css(style.ctaWrapper)}>
            <CtaButton {...cta} />
          </div>
        ) : null }
        { editions ? <AMPEditions editions={editions} editionEndpoint={editionEndpoint} /> : null }
      </div>
    </div>
  );
};

const mapThemeProps = (theme: PrimaryHeaderThemeProps) => {
  return {
    vrLineHeight: theme.vrLineHeight,
    backgroundColor: theme.backgroundColor,
    surfaceStyle: theme.surfaceStyle,
  };
};

export const AMPPrimaryHeader = (props: AMPPrimaryHeaderDataProps) => {
  const themeContext = useTheme(componentConfigThemeKey, mapThemeProps);
  return <ErrorBoundary><AMPPrimaryHeaderComponent {...themeContext} {...props} /></ErrorBoundary>;
};
